import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Pill, Player, run } from "@/components/competition/shared";

function Decklist({ tid, userId }) {
  const { data, error } = useQuery({ queryKey: ["tournament", tid, "deck", userId], retry: false, queryFn: () => api.get(`/tournaments/${tid}/entries/${userId}/deck`).then((r) => r.data) });
  if (error) return <p className="text-xs text-slate-500">Decklist not available.</p>;
  if (!data) return <p className="text-xs text-slate-500">Loading…</p>;
  const zones = data.entries.reduce((acc, e) => ({ ...acc, [e.zone]: [...(acc[e.zone] || []), e] }), {});
  return (
    <div className="mt-2 rounded-md border border-[#2A3447] bg-[#0B111E] p-3 text-xs" data-testid={`decklist-${userId}`}>
      <p className="font-mono text-slate-500">Locked {data.locked_at.slice(0, 16).replace("T", " ")} · fingerprint {data.hash.slice(0, 12)}</p>
      {Object.entries(zones).map(([z, list]) => <div key={z} className="mt-2"><p className="eyebrow text-[10px]">{z} ({list.reduce((s, e) => s + e.quantity, 0)})</p>
        <ul className="columns-2 text-slate-300">{list.map((e) => <li key={e.source + e.ref_id}>{e.quantity}× {e.name || "Hidden card"}</li>)}</ul></div>)}
      {!data.entries.length && <p className="mt-2 text-slate-500">This decklist has no cards.</p>}
    </div>
  );
}

function EntryRow({ e, t, canManage, refresh }) {
  const [open, setOpen] = useState(false);
  return (
    <li className="border-t border-[#2A3447] py-2" data-testid={`entry-${e.user?.username}`}>
      <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
        <span className="flex items-center gap-2"><Player user={e.user} /><Pill value={e.status} />{e.final_rank && <span className="font-mono text-xs text-amber-300">#{e.final_rank}</span>}</span>
        <span className="flex items-center gap-3 text-xs text-slate-400">
          {e.deck ? <>
            <span>{e.deck.name} · {e.deck.card_count} cards</span>
            {!e.deck.format_match && <span className="text-amber-300">format differs</span>}
            {e.deck.warnings?.length > 0 && <span className="text-amber-300" title={e.deck.warnings.join("\n")}>{e.deck.warnings.length} warnings</span>}
            <button className="text-aether-300 hover:underline" onClick={() => setOpen(!open)} data-testid={`entry-decklist-toggle-${e.user?.username}`}>{open ? "Hide list" : "Decklist"}</button>
          </> : <span>{e.has_deck ? (e.deck_locked ? "Decklist private" : "Deck chosen, not locked yet") : "No deck"}</span>}
          {canManage && ["registration", "check_in"].includes(t.status) && ["registered", "checked_in", "waitlisted"].includes(e.status) &&
            <button className="text-red-300 hover:underline" onClick={() => window.confirm(`Remove @${e.user?.username}?`) && run(() => api.delete(`/tournaments/${t.tournament_id}/entries/${e.user_id}`), "Registration removed", refresh)} data-testid={`entry-remove-${e.user?.username}`}>Remove</button>}
        </span>
      </div>
      {open && <Decklist tid={t.tournament_id} userId={e.user_id} />}
    </li>
  );
}

export function EntriesList({ t, canManage, refresh }) {
  const { data = [] } = useQuery({ queryKey: ["tournament", t.tournament_id, "entries"], queryFn: () => api.get(`/tournaments/${t.tournament_id}/entries`).then((r) => r.data.items) });
  if (!data.length) return <p className="text-sm text-slate-500" data-testid="entries-empty">No registrations yet.</p>;
  return <ul className="panel px-4 py-1" data-testid="entries-list">{data.map((e) => <EntryRow key={e.user_id} e={e} t={t} canManage={canManage} refresh={refresh} />)}</ul>;
}
